class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const cartItems = this.closest('cart-items');
      if (!cartItems) {
        console.error('No cart-items element found');
        return;
      }

      const isAddonProduct = ["addon", "card"].includes(this.getAttribute("data-is-addon"));
      const itemKey = this.getAttribute("data-item-key");

      if (!isAddonProduct) {
        cartItems.handleRemove(this.dataset.index, this, itemKey);
      } else {
        cartItems.removeItemByKey(itemKey, this.dataset.index);
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartRemoveAllButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();

      const cartItems = document.querySelector('cart-items');
      if (!cartItems) {
        console.error('No cart-items element found');
        return;
      }

      this.setAttribute('disabled', 'disabled');

      try {
        const sections = cartItems.getSectionsToRender().map((section) => section.section).filter(Boolean);
        const body = JSON.stringify({
          sections,
          sections_url: window.location.pathname
        });

        const response = await fetch("/cart/clear.js", {
          ...fetchConfig(),
          method: 'POST',
          body
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        let parsedState;
        try {
          parsedState = JSON.parse(text);
        } catch (e) {
          throw new Error(`Failed to parse response: ${text}`);
        }

        cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

        cartItems.getSectionsToRender().forEach((section) => {
          const element = document.getElementById(section.id);
          if (element && parsedState.sections && parsedState.sections[section.section]) {
            const elementToReplace = element.querySelector(section.selector) || element;
            elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          } else {
            console.warn(`Section ${section.id} not found or missing in response`);
          }
        });

        cartItems.updateQuantityLiveRegions(0, parsedState.item_count);

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: parsedState }
        }));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      } catch (error) {
        console.error('Error clearing cart:', error);
        const cartErrors = document.getElementById('cart-errors');
        if (cartErrors) {
          cartErrors.textContent = theme.cartStrings.error || 'Failed to clear cart';
          cartErrors.style.display = 'block';
        }
      } finally {
        cartItems.disableLoading();
        this.removeAttribute('disabled');
      }
    });
  }
}
customElements.define('cart-remove-all-button', CartRemoveAllButton);

class CartItems extends HTMLElement {
  constructor() {
      super();
      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
      this.cartErrors = document.getElementById('cart-errors');
      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
        .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
      this.lastRemovedItem = null;

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      this.addEventListener('change', this.debouncedOnChange.bind(this));
      // Add listener for quantity remove attempt
      this.addEventListener('quantity:remove-attempt', this.onQuantityRemoveAttempt.bind(this));
  }

  onQuantityRemoveAttempt(event) {
    const { index, itemKey } = event.detail;
    // Call handleRemove for main products, which will check for addons
    this.handleRemove(index, null, itemKey);
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((section) => section.section);
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: window.location.pathname
    });

    // Check if the item is an addon product
    const quantityInput = document.getElementById(`Quantity-${line}`);
    const isAddon = quantityInput && ["addon", "card"].includes(quantityInput.closest('quantity-input')?.dataset.isAddon);

    // If quantity is 0 and it's an addon, bypass modal and remove directly
    if (parseInt(quantity) === 0 && isAddon) {
      try {
        const itemKey = quantityInput.closest('quantity-input').dataset.itemKey;
        const parsedState = await this.removeItemByKey(itemKey, line);
        return parsedState;
      } catch (error) {
        console.error('Error removing addon item:', error);
        this.updateErrorLiveRegions(line, error.message || 'Failed to remove item');
        throw error;
      } finally {
        this.disableLoading();
      }
    }

    try {
      const response = await fetch(`${theme.routes.cart_change_url}`, {
        ...fetchConfig(),
        body
      });
      const text = await response.text();
      const parsedState = JSON.parse(text);

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

      if (parsedState.errors) {
        this.updateErrorLiveRegions(line, parsedState.errors);
      }

      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && parsedState.sections[section.section]) {
          const elementToReplace = element.querySelector(section.selector) || element;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      this.updateQuantityLiveRegions(line, parsedState.item_count);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && name) lineItem.querySelector(`[name="${name}"]`).focus();

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

      return parsedState;
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.updateErrorLiveRegions(line, error.message || 'Failed to update quantity');
      throw error;
    } finally {
      this.disableLoading();
    }
  }

  async handleRemove(line, button, itemKey) {
    try {
      console.log("Fetching latest cart data...");
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("Failed to fetch cart.js");
      const cart = await response.json();

      let mainProductCount = 0;
      let foundAddonProduct = false;
      let removingItem = null;

      for (const item of cart.items) {
        if (item.key === itemKey) {
          removingItem = { ...item, line, key: itemKey };
          this.lastRemovedItem = removingItem;
        }
        if (item.product_type !== "addon" && item.product_type !== "card") {
          mainProductCount++;
        } else {
          foundAddonProduct = true;
        }
      }

      if (!removingItem) {
        console.log("Item to remove not found. Proceeding with removal...");
        await this.removeItemByKey(itemKey, line);
        return;
      }

      if (mainProductCount === 1 && foundAddonProduct) {
        console.log("Last main product with addons. Showing modal.");
        this.showRemoveModal(line);
      } else {
        console.log("Removing item (other main products exist or no addons)...");
        await this.removeItemByKey(itemKey, line);
      }
    } catch (error) {
      console.error("Error handling remove:", error);
      this.cartErrors.textContent = theme.cartStrings.error || 'Failed to process removal';
    }
  }

  async removeItemByKey(itemKey, line = 0) {
    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((section) => section.section);
    const body = JSON.stringify({
      id: itemKey,
      quantity: 0,
      sections,
      sections_url: window.location.pathname
    });

    try {
      console.log("Removing item from cart:", itemKey);
      const response = await fetch(`${theme.routes.cart_change_url}`, {
        ...fetchConfig(),
        body
      });
      const text = await response.text();
      const parsedState = JSON.parse(text);

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

      if (parsedState.errors) {
        this.updateErrorLiveRegions(line, parsedState.errors);
      }

      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && parsedState.sections[section.section]) {
          const elementToReplace = element.querySelector(section.selector) || element;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      this.updateQuantityLiveRegions(line, parsedState.item_count);

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

      return parsedState;
    } catch (error) {
      console.error('Error removing item by key:', error);
      this.updateErrorLiveRegions(line, error.message || 'Failed to remove item');
      throw error;
    } finally {
      this.disableLoading();
    }
  }

  showRemoveModal(line) {
    let modal = document.querySelector("#removeModal");
    if (!modal) {
      modal = document.createElement("div");
      modal.id = "removeModal";
      modal.style.cssText = `
        width: 50rem;
        max-width: 90vw;
        display: none;
        position: fixed;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        background: white;
        padding: 20px;
        box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
        text-align: center;
        z-index: 1000;
      `;
      modal.innerHTML = `
        <p>Removing flower arrangements will also remove the addons. Are you sure?</p>
        <button id="modalRemove" style="
          background-color: #660033;
          color: white;
          padding: 10px 20px;
          border: none;
          cursor: pointer;
          margin: 10px;
        ">Remove All</button>
        <button id="modalCancel" style="
          background-color: white;
          color: #660033;
          padding: 10px 20px;
          border: 1px solid maroon;
          cursor: pointer;
          margin: 10px;
        ">Cancel</button>
      `;
      document.body.appendChild(modal);

      const removeBtn = modal.querySelector("#modalRemove");
      const cancelBtn = modal.querySelector("#modalCancel");

      removeBtn.addEventListener('mouseover', () => {
        removeBtn.style.backgroundColor = 'white';
        removeBtn.style.border = "1px solid #660033";
        removeBtn.style.color = '#660033';
      });
      removeBtn.addEventListener('mouseout', () => {
        removeBtn.style.backgroundColor = '#660033';
        removeBtn.style.color = 'white';
      });
    }

    modal.style.display = "block";

    const outsideClickListener = async (event) => {
      if (!modal.contains(event.target)) {
        await this.addBackToCart();
        modal.style.display = "none";
        document.removeEventListener('click', outsideClickListener);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', outsideClickListener);
    }, 0);

    const removeBtn = modal.querySelector("#modalRemove");
    const cancelBtn = modal.querySelector("#modalCancel");

    removeBtn.onclick = async () => {
      await this.clearCart();
      modal.style.display = "none";
      document.removeEventListener('click', outsideClickListener);
    };

    cancelBtn.onclick = async () => {
      await this.addBackToCart();
      modal.style.display = "none";
      document.removeEventListener('click', outsideClickListener);
    };
  }

  async clearCart() {
    try {
      console.log("Clearing cart...");
      const sections = this.getSectionsToRender().map((section) => section.section).filter(Boolean);
      const body = JSON.stringify({
        sections,
        sections_url: window.location.pathname
      });

      const response = await fetch("/cart/clear.js", {
        ...fetchConfig(),
        method: 'POST',
        body
      });

      if (!response.ok) throw new Error("Failed to clear cart");

      const text = await response.text();
      const parsedState = JSON.parse(text);

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && parsedState.sections && parsedState.sections[section.section]) {
          const elementToReplace = element.querySelector(section.selector) || element;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      this.updateQuantityLiveRegions(0, parsedState.item_count);
      this.lastRemovedItem = null;

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    } catch (error) {
      console.error("Error clearing cart:", error);
      this.cartErrors.textContent = theme.cartStrings.error || 'Failed to clear cart';
    }
  }

  async addBackToCart() {
    if (!this.lastRemovedItem) {
      console.error("No removed item found. Cannot add back.");
      return;
    }

    try {
      console.log("Checking cart for item:", this.lastRemovedItem.key);
      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok) throw new Error("Failed to fetch cart.js");
      const cart = await cartResponse.json();

      const existingItem = cart.items.find(item => item.key === this.lastRemovedItem.key);
      const sections = this.getSectionsToRender().map((section) => section.section).filter(Boolean);
      let parsedState;

      if (existingItem) {
        const targetQuantity = this.lastRemovedItem.quantity || 1;
        if (existingItem.quantity === targetQuantity) {
          console.log("Item already in cart with correct quantity:", targetQuantity);
          parsedState = { items: cart.items, item_count: cart.item_count, sections: {} };
        } else {
          console.log("Item found in cart. Restoring quantity:", targetQuantity);
          const body = JSON.stringify({
            id: this.lastRemovedItem.key,
            quantity: targetQuantity,
            sections,
            sections_url: window.location.pathname
          });

          const response = await fetch(`${theme.routes.cart_change_url}`, {
            ...fetchConfig(),
            body
          });

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = "Failed to update item quantity";
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.description || errorData.message || errorMessage;
            } catch (e) {
              // Non-JSON response
            }
            throw new Error(errorMessage);
          }

          const text = await response.text();
          parsedState = JSON.parse(text);
        }
      } else {
        console.log("Adding back to cart:", this.lastRemovedItem.product_id);
        const body = JSON.stringify({
          id: this.lastRemovedItem.variant_id || this.lastRemovedItem.id,
          quantity: this.lastRemovedItem.quantity || 1,
          properties: this.lastRemovedItem.properties || {},
          sections,
          sections_url: window.location.pathname
        });

        const response = await fetch("/cart/add.js", {
          ...fetchConfig(),
          body
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = "Failed to re-add item to cart";
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.description || errorData.message || errorMessage;
          } catch (e) {
            // Non-JSON response
          }
          throw new Error(errorMessage);
        }

        const text = await response.text();
        parsedState = JSON.parse(text);
      }

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && parsedState.sections && parsedState.sections[section.section]) {
          const elementToReplace = element.querySelector(section.selector) || element;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      this.updateQuantityLiveRegions(this.lastRemovedItem.line, parsedState.item_count);
      this.lastRemovedItem = null;

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    } catch (error) {
      console.error("Error adding item back to cart:", error);
      this.updateErrorLiveRegions(this.lastRemovedItem.line, error.message || 'Failed to add item back');
    }
  }

  onChange(event) {
    if (event.target === null) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getSectionsToRender() {
    let sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.orderSummary .js-contents',
      }
    ];
    if (document.querySelector('#main-cart-footer .free-shipping')) {
      sections.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.free-shipping',
      });
    }
    return sections;
  }

  // async updateQuantity(line, quantity, name) {
  //   this.enableLoading(line);
  //   const sections = this.getSectionsToRender().map((section) => section.section);
  //   const body = JSON.stringify({
  //     line,
  //     quantity,
  //     sections,
  //     sections_url: window.location.pathname
  //   });

  //   try {
  //     const response = await fetch(`${theme.routes.cart_change_url}`, {
  //       ...fetchConfig(),
  //       body
  //     });
  //     const text = await response.text();
  //     const parsedState = JSON.parse(text);

  //     this.classList.toggle('is-empty', parsedState.item_count === 0);
  //     const cartFooter = document.getElementById('main-cart-footer');
  //     if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

  //     if (parsedState.errors) {
  //       this.updateErrorLiveRegions(line, parsedState.errors);
  //     }

  //     this.getSectionsToRender().forEach((section) => {
  //       const element = document.getElementById(section.id);
  //       if (element && parsedState.sections[section.section]) {
  //         const elementToReplace = element.querySelector(section.selector) || element;
  //         elementToReplace.innerHTML = this.getSectionInnerHTML(
  //           parsedState.sections[section.section],
  //           section.selector
  //         );
  //       }
  //     });

  //     this.updateQuantityLiveRegions(line, parsedState.item_count);

  //     const lineItem = document.getElementById(`CartItem-${line}`);
  //     if (lineItem && name) lineItem.querySelector(`[name="${name}"]`).focus();

  //     document.dispatchEvent(new CustomEvent('cart:updated', {
  //       detail: { cart: parsedState }
  //     }));
  //     publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

  //     return parsedState;
  //   } catch (error) {
  //     console.error('Error updating quantity:', error);
  //     this.updateErrorLiveRegions(line, error.message || 'Failed to update quantity');
  //     throw error;
  //   } finally {
  //     this.disableLoading();
  //   }
  // }

  updateErrorLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  updateQuantityLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const quantityError = document.getElementById(`Line-item-error-${line}`);
      if (quantityError) {
        quantityError.querySelector('.cart-item__error-text')
          .innerHTML = theme.cartStrings.quantityError.replace(
            '[quantity]',
            document.getElementById(`Quantity-${line}`).value
          );
      }
    }

    this.currentItemCount = itemCount;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.add('cart__items--disabled');

    if (line) {
      const loadingOverlay = this.querySelectorAll('.loading-overlay')[line - 1];
      if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    }

    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.remove('cart__items--disabled');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
  }
}
customElements.define('cart-items', CartItems);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${theme.routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
    }, 300));
  }
}
customElements.define('cart-note', CartNote);

class DiscountCode extends HTMLElement {
  constructor() {
    super();

    if (isStorageSupported('session')) {
      this.setupDiscount();

      this.addEventListener('change', (event) => {
        window.sessionStorage.setItem('discount', event.target.value);
      });
    }
  }

  setupDiscount() {
    const discount = window.sessionStorage.getItem('discount');
    if (discount !== null) {
      this.querySelector('input[name="discount"]').value = discount;
    }
  }
}
customElements.define('discount-code', DiscountCode);

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.setupCountries();

    this.errors = this.querySelector('#ShippingCalculatorErrors');
    this.success = this.querySelector('#ShippingCalculatorSuccess');
    this.zip = this.querySelector('#ShippingCalculatorZip');
    this.country = this.querySelector('#ShippingCalculatorCountry');
    this.province = this.querySelector('#ShippingCalculatorProvince');
    this.button = this.querySelector('button');
    this.button.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector('ShippingCalculatorCountry', 'ShippingCalculatorProvince', {
        hideElement: 'ShippingCalculatorProvinceContainer'
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();

    this.errors.classList.add('hidden');
    this.success.classList.add('hidden');
    this.zip.classList.remove('invalid');
    this.country.classList.remove('invalid');
    this.province.classList.remove('invalid');
    this.button.classList.add('loading');
    this.button.setAttribute('disabled', true);

    const body = JSON.stringify({
      shipping_address: {
        zip: this.zip.value,
        country: this.country.value,
        province: this.province.value
      }
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.success.classList.remove('hidden');
          this.success.innerHTML = '';

          parsedState.shipping_rates.forEach((rate) => {
            const child = document.createElement('p');
            child.innerHTML = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
            this.success.appendChild(child);
          });
        } else {
          let errors = [];
          Object.entries(parsedState).forEach(([attribute, messages]) => {
            errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
          });

          this.errors.classList.remove('hidden');
          this.errors.querySelector('.errors').innerHTML = errors.join('; ');
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }
}
customElements.define('shipping-calculator', ShippingCalculator);